import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Zap,
  ArrowLeft,
  Building2,
  TrendingDown,
  Award,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackComparisonClick } from "@/lib/api";
import { toast } from "sonner";

const B2B_BENEFITS = [
  { title: "Volume voordeel", text: "Bedrijfstarieven zijn vaak 15-25% lager dan consumenten tarieven bij hetzelfde verbruik." },
  { title: "Eigen accountmanager", text: "Bij Essent Zakelijk krijg je een eigen vast aanspreekpunt voor je vragen, contracten en facturatie." },
  { title: "Flexibele contracten", text: "Vast, variabel of dynamisch — gekoppeld aan de marktprijs voor maximale grip." },
  { title: "KvK-voorwaarden", text: "Speciaal voor BV's, ZZP'ers, eenmanszaken en VOFs — geen consumenten gedoe." },
];

export default function Zakelijk() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [kwh, setKwh] = useState("");
  const [companySize, setCompanySize] = useState("");

  const handleEssentClick = async () => {
    setLoading(true);
    try {
      const r = await trackComparisonClick({ provider_id: "essent_zakelijk" });
      if (r.url) {
        window.open(r.url, "_blank", "noopener,noreferrer");
      }
    } catch {
      // Fallback to direct URL if tracking fails
      window.open("https://www.essent.nl/zakelijk", "_blank", "noopener,noreferrer");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  // Estimated savings based on consumption tier
  const estimatedSavings = (() => {
    const v = parseInt(kwh) || 0;
    if (v >= 50000) return 2500;
    if (v >= 25000) return 1400;
    if (v >= 10000) return 750;
    if (v >= 5000) return 400;
    return 200;
  })();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="b2b-logo-link">
            <div className="h-9 w-9 rounded-xl bg-blue-700 text-white grid place-items-center shadow-sm">
              <Zap className="h-5 w-5" />
            </div>
            <span className="font-heading font-bold text-base sm:text-lg text-slate-900">EnergieKontract</span>
          </Link>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-slate-600 hover:text-blue-700 flex items-center gap-1.5 transition-colors"
            data-testid="b2b-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Particulier
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-16">
        {/* Hero */}
        <section className="text-center mb-12 sm:mb-16">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
            <Building2 className="h-3.5 w-3.5" />
            Zakelijk · KvK
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
            Bedrijfsenergie die
            <br />
            <span className="text-blue-700">écht voordeel</span> oplevert
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Voor BV's, ZZP'ers, eenmanszaken en VOFs. Krijg toegang tot scherpe zakelijke tarieven via Essent Zakelijk — vaak honderden tot duizenden euro's per jaar voordeel.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
            <button
              onClick={handleEssentClick}
              disabled={loading}
              className="group inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl px-7 py-4 shadow-[0_12px_32px_-12px_rgba(220,38,38,0.5)] transition-all hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="b2b-hero-cta-essent"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Vraag offerte aan bij Essent Zakelijk
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              100% gratis offerte
            </span>
            <span>·</span>
            <span>Vrijblijvend</span>
            <span>·</span>
            <span>Reactie binnen 1 werkdag</span>
          </div>
        </section>

        {/* Savings calculator */}
        <section className="mb-12">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.08)]" data-testid="b2b-savings-calc">
            <div className="flex items-start gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center flex-shrink-0">
                <TrendingDown className="h-5 w-5" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Quick check</span>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1">
                  Hoeveel kun je besparen?
                </h2>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="b2b-kwh" className="text-sm font-semibold text-slate-800 mb-2 block">
                  Jaarverbruik bedrijf (kWh)
                </Label>
                <Input
                  id="b2b-kwh"
                  type="number"
                  inputMode="numeric"
                  value={kwh}
                  onChange={(e) => setKwh(e.target.value)}
                  placeholder="bv. 25000"
                  className="h-12 text-base"
                  data-testid="b2b-kwh-input"
                />
                <p className="mt-1.5 text-xs text-slate-500">Vind je dit op je laatste jaarafrekening</p>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-800 mb-2 block">Bedrijfsgrootte</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { id: "zzp", label: "ZZP" },
                    { id: "mkb", label: "MKB" },
                    { id: "grootzakelijk", label: "Groot" },
                  ].map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setCompanySize(s.id)}
                      className={`px-3 py-3 rounded-lg border-2 font-semibold text-sm transition-all ${
                        companySize === s.id
                          ? "bg-blue-700 text-white border-blue-700"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`b2b-size-${s.id}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {kwh && parseInt(kwh) > 0 && (
              <div className="mt-6 bg-gradient-to-br from-emerald-50 to-blue-50 border-2 border-emerald-200 rounded-2xl p-5 sm:p-6" data-testid="b2b-savings-result">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Geschatte jaarlijkse besparing</div>
                    <div className="mt-1 font-heading text-4xl sm:text-5xl font-bold text-emerald-700">
                      €{estimatedSavings.toLocaleString("nl-NL")}<span className="text-base font-normal text-slate-500">/jaar</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-600">
                      Op basis van {parseInt(kwh).toLocaleString("nl-NL")} kWh/jr — gemiddelde besparing van zakelijke klanten t.o.v. consumententarieven.
                    </p>
                  </div>
                  <button
                    onClick={handleEssentClick}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-xl px-5 py-3 shadow-sm transition-all hover:-translate-y-0.5 disabled:opacity-60 whitespace-nowrap"
                    data-testid="b2b-savings-cta"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <>
                        Bereken bij Essent
                        <ExternalLink className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-6">
            Waarom Essent Zakelijk?
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {B2B_BENEFITS.map((b, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid={`b2b-benefit-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl bg-blue-50 text-blue-700 grid place-items-center flex-shrink-0">
                    <Award className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 leading-tight">{b.title}</h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{b.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mb-8">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-3xl p-8 sm:p-12 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,white_0%,transparent_50%)]"></div>
            <div className="relative">
              <Sparkles className="h-10 w-10 text-amber-400 mx-auto mb-4" />
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Klaar om als bedrijf
                <br />écht slim te besparen?
              </h2>
              <p className="mt-3 text-slate-300 max-w-xl mx-auto">
                Krijg in minder dan 24 uur een persoonlijke zakelijke offerte van Essent — vrijblijvend en zonder gedoe.
              </p>
              <button
                onClick={handleEssentClick}
                disabled={loading}
                className="mt-7 inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-xl px-7 py-4 shadow-[0_12px_32px_-12px_rgba(245,158,11,0.5)] transition-all hover:-translate-y-0.5 disabled:opacity-60"
                data-testid="b2b-final-cta"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>
                    Vraag mijn gratis offerte aan
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Trust footer */}
        <p className="text-[11px] text-slate-500 text-center max-w-2xl mx-auto">
          EnergieKontract ontvangt een vergoeding als je via deze pagina overstapt — dit beïnvloedt de prijs die jij betaalt niet. Het advies blijft onafhankelijk en gebaseerd op jouw bedrijfssituatie.
        </p>
      </main>
    </div>
  );
}
